import { CalendarX2, CircleCheck, CirclePause, Clock, Sparkles, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { type BillingState, formatDay } from './billingState'

/**
 * Icon, icon colour and edge per state.
 *
 * 🔴 Same build as `VerdictHeader` (validation): the status colour sits ONLY on the icon and the 2-px edge above
 * the line — the text stays `foreground`. Colour is never the only signal: icon and word say the same.
 * `green-800`, not `success`: `success` measured 2.98:1 on white (2026-10-10).
 */
const STATE_STYLE: Record<
  BillingState,
  { icon: typeof CircleCheck; iconClass: string; edge: string }
> = {
  active: { icon: CircleCheck, iconClass: 'text-green-800', edge: 'border-green-800' },
  trial: { icon: Clock, iconClass: 'text-muted-foreground', edge: 'border-border' },
  canceled: { icon: CalendarX2, iconClass: 'text-muted-foreground', edge: 'border-border' },
  pastDue: { icon: TriangleAlert, iconClass: 'text-destructive', edge: 'border-destructive' },
  paused: { icon: CirclePause, iconClass: 'text-muted-foreground', edge: 'border-border' },
  free: { icon: Sparkles, iconClass: 'text-muted-foreground', edge: 'border-border' },
}

export interface BillingHeaderProps {
  state: BillingState
  /** Plan name from `getPlan` — falls back to the word for the free plan. */
  planName?: string | null
  /** The date the state sentence names (renewal, trial end, end of the plan). */
  date?: Date
  locale: string
  /** Buttons on the right — „Change plan", „Manage payment & invoices". */
  actions?: ReactNode
}

/**
 * Plan and status as the headline of the page — „Growth — active, renews on 11 Nov 2026".
 *
 * The line wraps below `sm`: the buttons drop under the headline and keep their one-line labels (`whitespace-nowrap`),
 * nothing scrolls sideways.
 */
export function BillingHeader({ state, planName, date, locale, actions }: BillingHeaderProps) {
  const { t } = useTranslation()
  const { icon: Icon, iconClass, edge } = STATE_STYLE[state]
  const label = planName?.trim() || t('billingPage.freePlan')
  const day = date ? formatDay(date, locale) : ''

  const detail: Record<BillingState, string> = {
    active: t('billingPage.state.active', { date: day }),
    trial: t('billingPage.state.trial', { date: day }),
    canceled: t('billingPage.state.canceled', { date: day }),
    pastDue: t('billingPage.state.pastDue'),
    paused: t('billingPage.state.paused'),
    free: t('billingPage.state.free'),
  }

  return (
    <div
      className={`flex flex-wrap items-start justify-between gap-x-6 gap-y-3 border-t-2 pt-3 ${edge}`}
      data-testid="billing-header"
      data-state={state}
    >
      <h2 className="flex min-w-0 flex-[1_1_18rem] items-start gap-2 text-2xl font-semibold tracking-tight">
        <Icon className={`mt-1 h-6 w-6 shrink-0 ${iconClass}`} aria-hidden="true" />
        <span className="min-w-0 [overflow-wrap:anywhere]">
          <span data-testid="billing-header-plan">{label}</span>
          <span className="font-normal text-muted-foreground" data-testid="billing-header-detail">
            {` — ${detail[state]}`}
          </span>
        </span>
      </h2>
      {actions && <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>}
    </div>
  )
}
